import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { TitleProvider } from "./context/TitleContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { setupOfflineRequestListener } from "./shared/utils.js/offlineRequestManager";
import { prewarmOfflineCache } from "./shared/utils.js/offlinePrefetcher";
import { getToken } from "./shared/utils.js/utils";
import { getGuardedInputValue } from "./shared/utils.js/fieldValidation";
import Layout from "./shared/components/Layout";
import Login from "./features/pages/Auth/Login";
import Profile from "./features/pages/Profile/Profile";
import PrivateRoute from "./shared/components/PrivateRoute";
import HrHome from "./features/pages/Hr/HrHome";
import Employees from "./features/pages/Hr/employees/Employees";
import AddEmployee from "./features/pages/Hr/employees/AddEmployee";
import EditEmployee from "./features/pages/Hr/employees/EditEmployee";
import EmployeeDetails from "./features/pages/Hr/employees/EmployeeDetails";
import Attendance from "./features/pages/Hr/attendance/Attendance";
import Salaries from "./features/pages/Hr/salaries/Salaries";
import AddSalary from "./features/pages/Hr/salaries/AddSalary";
import EditSalary from "./features/pages/Hr/salaries/EditSalary";
import Vacations from "./features/pages/Hr/vacations/Vacations";
import AddVacation from "./features/pages/Hr/vacations/AddVacation";
import EditVacation from "./features/pages/Hr/vacations/EditVacation";
import VacationDetails from "./features/pages/Hr/vacations/VacationDetails";
import LeaveRequests from "./features/pages/Hr/leave-requests/LeaveRequests";
import AddLeaveRequest from "./features/pages/Hr/leave-requests/AddLeaveRequest";
import EditLeaveRequest from "./features/pages/Hr/leave-requests/EditLeaveRequest";
import LeaveRequestDetails from "./features/pages/Hr/leave-requests/LeaveRequestDetails";
import SalaryDetails from "./features/pages/Hr/salaries/SalaryDetails";
import Payslips from "./features/pages/Hr/payslips/Payslips";
import PayslipDetails from "./features/pages/Hr/payslips/PayslipDetails";
import GeneratePayslip from "./features/pages/Hr/payslips/GeneratePayslip";
import PayslipsSummary from "./features/pages/Hr/payslips/PayslipsSummary";
import PayslipsStatement from "./features/pages/Hr/payslips/PayslipsStatement";
import PayrollReport from "./features/pages/Hr/reports/PayrollReport";
import AttendanceReport from "./features/pages/Hr/reports/AttendanceReport";
import LeaveReport from "./features/pages/Hr/reports/LeaveReport";
import WorkingHoursReport from "./features/pages/Hr/reports/WorkingHoursReport";
import LoansReport from "./features/pages/Hr/reports/LoansReport";
import PenaltiesReport from "./features/pages/Hr/reports/PenaltiesReport";
import Shifts from "./features/pages/Hr/shifts/Shifts";
import AddShift from "./features/pages/Hr/shifts/AddShift";
import EditShift from "./features/pages/Hr/shifts/EditShift";
import ShiftDetails from "./features/pages/Hr/shifts/ShiftDetails";
import JobTitles from "./features/pages/Hr/job-titles/JobTitles";
import AddJobTitle from "./features/pages/Hr/job-titles/AddJobTitle";
import EditJobTitle from "./features/pages/Hr/job-titles/EditJobTitle";
import JobTitleDetails from "./features/pages/Hr/job-titles/JobTitleDetails";
import Departments from "./features/pages/Hr/departments/Departments";
import AddDepartment from "./features/pages/Hr/departments/AddDepartment";
import EditDepartment from "./features/pages/Hr/departments/EditDepartment";
import DepartmentDetails from "./features/pages/Hr/departments/DepartmentDetails";
import PenaltyTypes from "./features/pages/Hr/PenaltyTypes/PenaltyTypes";
import AddPenaltyType from "./features/pages/Hr/PenaltyTypes/AddPenaltyType";
import EditPenaltyType from "./features/pages/Hr/PenaltyTypes/EditPenaltyType";
import EmployeePenalties from "./features/pages/Hr/EmployeePenalties/EmployeePenalties";
import AddEmployeePenalty from "./features/pages/Hr/EmployeePenalties/AddEmployeePenalty";
import EditEmployeePenalty from "./features/pages/Hr/EmployeePenalties/EditEmployeePenalty";
import EmployeeLoans from "./features/pages/Hr/EmployeeLoans/EmployeeLoans";
import AddEmployeeLoan from "./features/pages/Hr/EmployeeLoans/AddEmployeeLoan";
import EditEmployeeLoan from "./features/pages/Hr/EmployeeLoans/EditEmployeeLoan";
import Contractors from "./features/pages/Contracting/contractors/Contractors";
import AddContractor from "./features/pages/Contracting/contractors/AddContractor";
import EditContractor from "./features/pages/Contracting/contractors/EditContractor";
import ContractorDetails from "./features/pages/Contracting/contractors/ContractorDetails";
import Engineers from "./features/pages/Hr/Engineers/Engineers";
import AddEngineer from "./features/pages/Hr/Engineers/AddEngineer";
import EditEngineer from "./features/pages/Hr/Engineers/EditEngineer";
import ViewEngineer from "./features/pages/Hr/Engineers/ViewEngineer";
import AccountsTree from "./features/pages/Accounting/AccountsTree/AccountsTree";
import CostCentersTree from "./features/pages/Accounting/CostCentersTree/CostCentersTree";
import JournalEntries from "./features/pages/Accounting/JournalEntries/JournalEntries";
import AddJournalEntry from "./features/pages/Accounting/JournalEntries/AddJournalEntry";
import EditJournalEntry from "./features/pages/Accounting/JournalEntries/EditJournalEntry";
import JournalEntryDetails from "./features/pages/Accounting/JournalEntries/JournalEntryDetails";
import Currencies from "./features/pages/Accounting/Currencies/Currencies";
import AddCurrency from "./features/pages/Accounting/Currencies/AddCurrency";
import EditCurrency from "./features/pages/Accounting/Currencies/EditCurrency";
import CurrencyDetails from "./features/pages/Accounting/Currencies/CurrencyDetails";
import ExchangeRates from "./features/pages/Accounting/ExchangeRates/ExchangeRates";
import AddExchangeRate from "./features/pages/Accounting/ExchangeRates/AddExchangeRate";
import EditExchangeRate from "./features/pages/Accounting/ExchangeRates/EditExchangeRate";
import ExchangeRateDetails from "./features/pages/Accounting/ExchangeRates/ExchangeRateDetails";
import Vendors from "./features/pages/Hr/Vendors/Vendors";
import AddVendor from "./features/pages/Hr/Vendors/AddVendor";
import EditVendor from "./features/pages/Hr/Vendors/EditVendor";
import VendorDetails from "./features/pages/Hr/Vendors/VendorDetails";
import TrialBalance from "./features/pages/Accounting/Reports/TrialBalance";
import AccountStatement from "./features/pages/Accounting/Reports/AccountStatement";
import IncomeStatement from "./features/pages/Accounting/Reports/IncomeStatement";
import CustomerStatement from "./features/pages/Accounting/Reports/CustomerStatement";
import VendorStatement from "./features/pages/Accounting/Reports/VendorStatement";
import BalanceSheet from "./features/pages/Accounting/Reports/BalanceSheet";
import CashFlowStatement from "./features/pages/Accounting/Reports/CashFlowStatement";
import CustomerAging from "./features/pages/Accounting/Reports/CustomerAging";
import VendorAging from "./features/pages/Accounting/Reports/VendorAging";
import GeneralLedger from "./features/pages/Accounting/Reports/GeneralLedger";
import CostCenterReport from "./features/pages/Accounting/Reports/CostCenterReport";
import AccountStatementReport from "./features/pages/Accounting/Reports/AccountStatementReport";
import Customers from "./features/pages/Accounting/Customers/Customers";
import AddCustomer from "./features/pages/Accounting/Customers/AddCustomer";
import EditCustomer from "./features/pages/Accounting/Customers/EditCustomer";
import CustomerDetails from "./features/pages/Accounting/Customers/CustomerDetails";
import Banks from "./features/pages/Accounting/Banks/Banks";
import AddBank from "./features/pages/Accounting/Banks/AddBank";
import EditBank from "./features/pages/Accounting/Banks/EditBank";
import BankDetails from "./features/pages/Accounting/Banks/BankDetails";
import Safes from "./features/pages/Accounting/Safes/Safes";
import AddSafe from "./features/pages/Accounting/Safes/AddSafe";
import EditSafe from "./features/pages/Accounting/Safes/EditSafe";
import SafeDetails from "./features/pages/Accounting/Safes/SafeDetails";
import AccountLinks from "./features/pages/Accounting/AccountLinks/AccountLinks";
import FinancialPeriods from "./features/pages/Accounting/FinancialPeriods/FinancialPeriods";
import AddFinancialPeriod from "./features/pages/Accounting/FinancialPeriods/AddFinancialPeriod";
import EditFinancialPeriod from "./features/pages/Accounting/FinancialPeriods/EditFinancialPeriod";
import FinancialPeriodDetails from "./features/pages/Accounting/FinancialPeriods/FinancialPeriodDetails";
import ReceiptVouchers from "./features/pages/Accounting/ReceiptVouchers/ReceiptVouchers";
import AddReceiptVoucher from "./features/pages/Accounting/ReceiptVouchers/AddReceiptVoucher";
import ReceiptVoucherDetails from "./features/pages/Accounting/ReceiptVouchers/ReceiptVoucherDetails";
import EditReceiptVoucher from "./features/pages/Accounting/ReceiptVouchers/EditReceiptVoucher";
import PaymentVouchers from "./features/pages/Accounting/PaymentVouchers/PaymentVouchers";
import AddPaymentVoucher from "./features/pages/Accounting/PaymentVouchers/AddPaymentVoucher";
import PaymentVoucherDetails from "./features/pages/Accounting/PaymentVouchers/PaymentVoucherDetails";
import OpeningBalances from "./features/pages/Accounting/OpeningBalances/OpeningBalances";
import AddOpeningBalance from "./features/pages/Accounting/OpeningBalances/AddOpeningBalance";
import EditOpeningBalance from "./features/pages/Accounting/OpeningBalances/EditOpeningBalance";
import NotesReceivable from "./features/pages/Accounting/NotesReceivable/NotesReceivable";
import AddNoteReceivable from "./features/pages/Accounting/NotesReceivable/AddNoteReceivable";
import NoteReceivableDetails from "./features/pages/Accounting/NotesReceivable/NoteReceivableDetails";
import EditNoteReceivable from "./features/pages/Accounting/NotesReceivable/EditNoteReceivable";
import NotesPayable from "./features/pages/Accounting/NotesPayable/NotesPayable";
import AddNotePayable from "./features/pages/Accounting/NotesPayable/AddNotePayable";
import NotePayableDetails from "./features/pages/Accounting/NotesPayable/NotePayableDetails";
import EditNotePayable from "./features/pages/Accounting/NotesPayable/EditNotePayable";
import Taxes from "./features/pages/Accounting/Taxes/Taxes";
import AddTax from "./features/pages/Accounting/Taxes/AddTax";
import EditTax from "./features/pages/Accounting/Taxes/EditTax";
import TaxDetails from "./features/pages/Accounting/Taxes/TaxDetails";
import Projects from "./features/pages/Contracting/Projects/Projects";
import AddProject from "./features/pages/Contracting/Projects/AddProject";
import EditProject from "./features/pages/Contracting/Projects/EditProject";
import ProjectDetails from "./features/pages/Contracting/Projects/ProjectDetails";
import Equipments from "./features/pages/Contracting/Equipments/Equipments";
import AddEquipment from "./features/pages/Contracting/Equipments/AddEquipment";
import EditEquipment from "./features/pages/Contracting/Equipments/EditEquipment";
import ViewEquipment from "./features/pages/Contracting/Equipments/ViewEquipment";
import EquipmentsExpenses from "./features/pages/Contracting/EquipmentsExpenses/EquipmentsExpenses";
import AddEquipmentExpenses from "./features/pages/Contracting/EquipmentsExpenses/AddEquipmentExpenses";
import EditEquipmentExpenses from "./features/pages/Contracting/EquipmentsExpenses/EditEquipmentExpenses";
import ViewEquipmentExpenses from "./features/pages/Contracting/EquipmentsExpenses/ViewEquipmentExpenses";
import Contracts from "./features/pages/Contracting/Contracts/Contracts";
import AddContract from "./features/pages/Contracting/Contracts/AddContract";
import EditContract from "./features/pages/Contracting/Contracts/EditContract";
import ViewContract from "./features/pages/Contracting/Contracts/ViewContract";
import ContractFromQuotation from "./features/pages/Contracting/Contracts/ContractFromQuotation";
import Tasks from "./features/pages/Contracting/Tasks/Tasks";
import AddTasks from "./features/pages/Contracting/Tasks/AddTasks";
import EditTasks from "./features/pages/Contracting/Tasks/EditTasks";
import ViewTasks from "./features/pages/Contracting/Tasks/viewTasks";
import ProgressBill from "./features/pages/Contracting/ProgressBill/ProgressBill";
import AddProgressBill from "./features/pages/Contracting/ProgressBill/AddProgressBill";
import EditProgressBill from "./features/pages/Contracting/ProgressBill/EditProgressBill";
import ViewProgressBill from "./features/pages/Contracting/ProgressBill/ViewProgressBill";
import Boreholes from "./features/pages/Contracting/Boreholes/Boreholes";
import AddBorehole from "./features/pages/Contracting/Boreholes/AddBorehole";
import BoreholeDetails from "./features/pages/Contracting/Boreholes/BoreholeDetails";
import BoreholeDefaults from "./features/pages/Contracting/Boreholes/BoreholeDefaults";
import BoreholeLegends from "./features/pages/Contracting/Boreholes/BoreholeLegends";
import AuditLogs from "./features/pages/AuditLogs/AuditLogs";
import ViewAuditLog from "./features/pages/AuditLogs/ViewAuditLog";
import CleanupAuditLogs from "./features/pages/AuditLogs/CleanupAuditLogs";
import CompareAuditLogs from "./features/pages/AuditLogs/CompareAuditLogs";
import AuditLogStatistics from "./features/pages/AuditLogs/AuditLogStatistics";
import ModelAuditLogs from "./features/pages/AuditLogs/ModelAuditLogs";
import Quotations from "./features/pages/Accounting/Quotations/Quotations";
import AddQuotation from "./features/pages/Accounting/Quotations/AddQuotation";
import QuotationDetails from "./features/pages/Accounting/Quotations/QuotationDetails";
import OpeningBalanceDetails from "./features/pages/Accounting/OpeningBalances/OpeningBalanceDetails";
import NotFound from "./shared/components/NotFound";
import "./App.css";
import Units from "./features/pages/Accounting/Units/Units";
import AddUnits from "./features/pages/Accounting/Units/addUnits";
import EditUnits from "./features/pages/Accounting/Units/EditUnits";
import UnitsDetails from "./features/pages/Accounting/Units/UnitsDetails";
import LabTests from "./features/pages/Contracting/LabTests/LabTests";
import AddLabTests from "./features/pages/Contracting/LabTests/addLabTests";
import EditLabTests from "./features/pages/Contracting/LabTests/editLabTests";
import LabTestsDetails from "./features/pages/Contracting/LabTests/LabTestsDetails";
import QuotationsReport from "./features/pages/Reports/Projects/QuotationsReport";
import ContractsReport from "./features/pages/Reports/Projects/ContractsReport";
import ProgressBillsReport from "./features/pages/Reports/Projects/ProgressBillsReport";
import TasksReport from "./features/pages/Reports/Projects/TasksReport";
import ProjectSummary from "./features/pages/Reports/Projects/ProjectSummary";
import ProjectsOverview from "./features/pages/Reports/Projects/ProjectsOverview";
import ProjectTests from "./features/pages/Reports/Projects/ProjectTests";
import Roles from "./features/pages/Roles/Roles";
import WebsiteSettings from "./features/pages/Website/WebsiteSettings";
import WebsiteMessages from "./features/pages/Website/WebsiteMessages";
import AddRole from "./features/pages/Roles/AddRole";
import EditRole from "./features/pages/Roles/EditRole";
import AssignPermissions from "./features/pages/Roles/AssignPermissions";
import Permissions from "./features/pages/Permissions/Permissions";
import AddPermission from "./features/pages/Permissions/AddPermission";
import EditPermission from "./features/pages/Permissions/EditPermission";
import Users from "./features/pages/Users/Users";
import AddUser from "./features/pages/Users/AddUser";
import EditUser from "./features/pages/Users/EditUser";
import ChangePassword from "./features/pages/Users/ChangePassword";
import UserDetails from "./features/pages/Users/UserDetails";
import EditPaymentVoucher from "./features/pages/Accounting/PaymentVouchers/EditPaymentVoucher";

// Global 401 interceptor - redirect to login when session expires
axios.interceptors.response.use(
  response => response,
  error => {
    if (
      error.response?.status === 401 &&
      !window.location.pathname.includes("/login")
    ) {
      localStorage.removeItem("useralbaraqawy");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

function App() {
  const { user } = useSelector(state => state.auth);
  const { t, i18n } = useTranslation();

  // Keep form input values inside the expected shape before page-level handlers read them.
  useEffect(() => {
    const updateInputValue = (target, nextValue) => {
      if (target.value === nextValue) return;
      const selectionStart = target.selectionStart;
      const selectionEnd = target.selectionEnd;
      target.value = nextValue;
      if (
        typeof selectionStart === "number" &&
        typeof selectionEnd === "number" &&
        typeof target.setSelectionRange === "function"
      ) {
        const safePosition = Math.min(selectionStart, String(nextValue).length);
        target.setSelectionRange(safePosition, safePosition);
      }
    };

    const isGuardedField = target =>
      target &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA") &&
      ![
        "checkbox",
        "radio",
        "file",
        "date",
        "time",
        "datetime-local",
        "month",
        "password",
        "email",
      ].includes(String(target.type || "").toLowerCase());

    const handleWheel = e => {
      if (e.target.type === "number") {
        e.target.blur();
      }
    };

    const handleKeyDown = e => {
      if (
        e.target.type === "number" &&
        (e.key === "-" || e.key === "e" || e.key === "E" || e.key === "+")
      ) {
        e.preventDefault();
      }
    };

    const handleInput = e => {
      if (isGuardedField(e.target)) {
        updateInputValue(e.target, getGuardedInputValue(e.target));
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: true });
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("input", handleInput, true);
    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("input", handleInput, true);
    };
  }, []);

  // Universal Offline Prewarm - Trigger on app load OR right after login
  useEffect(() => {
    const token = getToken();
    if (
      token &&
      localStorage.getItem("enable_offline_prewarm") === "true" &&
      localStorage.getItem("offline_prewarm_done") !== "true"
    ) {
      const delay = user ? 1500 : 0;
      prewarmOfflineCache(delay);
    }
  }, [user]);

  // تنظيف التوستات عند تحميل التطبيق لمنع ظهور التوستات القديمة
  useEffect(() => {
    // Setup offline request listener to retry pending requests
    setupOfflineRequestListener();

    // Listen for successful offline requests retry and show toast
    const handleOfflineRequestsRetried = event => {
      // Only show sync toast if an offline add was performed in this session
      const offlineAddPerformed =
        localStorage.getItem("offline_add_performed") === "true";
      if (event?.detail?.count > 0 && offlineAddPerformed) {
        const toastId = toast.success(
          t("app.offlineSyncSuccess", { count: event.detail.count }),
          {
            toastId: "offline-sync-success",
            rtl: i18n.language === "ar",
            onClose: () => {
              window.location.reload();
            },
          },
        );
        // Reset the flag after showing the toast
        localStorage.removeItem("offline_add_performed");
      }
    };
    window.addEventListener(
      "offlineRequestsRetried",
      handleOfflineRequestsRetried,
    );
    return () => {
      window.removeEventListener(
        "offlineRequestsRetried",
        handleOfflineRequestsRetried,
      );
    };
  }, [i18n.language]);

  return (
    <TitleProvider>
      <Router>
        <div className="App" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* <Route path="/register" element={<Register />} /> */}

            {/* جميع المسارات محمية بـ PrivateRoute ما عدا /login */}
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              {/* جميع المسارات الفرعية هنا */}
              <Route index element={<HrHome />} />
              {/* HR Routes */}
              <Route path="employees" element={<Employees />} />
              <Route path="add-employee" element={<AddEmployee />} />
              <Route path="employees/view/:id" element={<EmployeeDetails />} />
              <Route path="edit-employee/:id" element={<EditEmployee />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="salaries" element={<Salaries />} />
              <Route path="add-salary" element={<AddSalary />} />
              <Route path="edit-salary/:id" element={<EditSalary />} />
              <Route path="vacations" element={<Vacations />} />
              <Route path="vacations/add" element={<AddVacation />} />
              <Route path="vacations/edit/:id" element={<EditVacation />} />
              <Route
                path="vacations/details/:id"
                element={<VacationDetails />}
              />
              <Route path="leave-requests" element={<LeaveRequests />} />
              <Route path="leave-requests/add" element={<AddLeaveRequest />} />
              <Route
                path="leave-requests/edit/:id"
                element={<EditLeaveRequest />}
              />
              <Route
                path="leave-requests/details/:id"
                element={<LeaveRequestDetails />}
              />
              <Route path="salaries" element={<Salaries />} />
              <Route path="salaries/add" element={<AddSalary />} />
              <Route path="salaries/edit/:id" element={<EditSalary />} />
              <Route path="salaries/details/:id" element={<SalaryDetails />} />
              <Route path="payslips" element={<Payslips />} />
              <Route path="payslips/generate" element={<GeneratePayslip />} />
              <Route path="payslips/summary" element={<PayslipsSummary />} />
              <Route
                path="payslips/statement"
                element={<PayslipsStatement />}
              />
              <Route path="payslips/:id" element={<PayslipDetails />} />
              <Route
                path="payslips/:id/post-journal"
                element={<PayslipDetails />}
              />
              <Route path="shifts" element={<Shifts />} />
              <Route path="add-shift" element={<AddShift />} />
              <Route path="job-titles" element={<JobTitles />} />
              <Route path="add-job-title" element={<AddJobTitle />} />
              <Route path="job-titles/add" element={<AddJobTitle />} />
              <Route path="job-titles/edit/:id" element={<EditJobTitle />} />
              <Route
                path="job-titles/details/:id"
                element={<JobTitleDetails />}
              />
              <Route path="departments" element={<Departments />} />
              <Route path="departments/add" element={<AddDepartment />} />
              <Route path="departments/edit/:id" element={<EditDepartment />} />
              <Route
                path="departments/details/:id"
                element={<DepartmentDetails />}
              />
              <Route path="penalty-types" element={<PenaltyTypes />} />
              <Route path="penalty-types/add" element={<AddPenaltyType />} />
              <Route
                path="penalty-types/edit/:id"
                element={<EditPenaltyType />}
              />
              <Route
                path="employee-penalties"
                element={<EmployeePenalties />}
              />
              <Route
                path="employee-penalties/add"
                element={<AddEmployeePenalty />}
              />
              <Route
                path="employee-penalties/edit/:id"
                element={<EditEmployeePenalty />}
              />
              <Route path="employee-loans" element={<EmployeeLoans />} />
              <Route path="employee-loans/add" element={<AddEmployeeLoan />} />
              <Route
                path="employee-loans/edit/:id"
                element={<EditEmployeeLoan />}
              />
              <Route path="contractors" element={<Contractors />} />
              <Route path="contractors/add" element={<AddContractor />} />
              <Route path="contractors/edit/:id" element={<EditContractor />} />
              <Route path="contractors/:id" element={<ContractorDetails />} />
              <Route path="engineers" element={<Engineers />} />
              <Route path="engineers/add" element={<AddEngineer />} />
              <Route path="engineers/edit/:id" element={<EditEngineer />} />
              <Route path="engineers/view/:id" element={<ViewEngineer />} />
              <Route path="reports/payroll" element={<PayrollReport />} />
              <Route path="reports/attendance" element={<AttendanceReport />} />
              <Route path="reports/leave" element={<LeaveReport />} />
              <Route
                path="hr-reports/working-hours"
                element={<WorkingHoursReport />}
              />
              <Route path="hr-reports/loans" element={<LoansReport />} />
              <Route
                path="hr-reports/penalties"
                element={<PenaltiesReport />}
              />
              <Route path="accounts/tree" element={<AccountsTree />} />
              <Route path="cost-centers/tree" element={<CostCentersTree />} />
              <Route path="journal-entries" element={<JournalEntries />} />
              <Route path="journal-entries/add" element={<AddJournalEntry />} />
              <Route
                path="journal-entries/edit/:id"
                element={<EditJournalEntry />}
              />
              <Route
                path="journal-entries/view/:id"
                element={<JournalEntryDetails />}
              />
              <Route path="receipt-vouchers" element={<ReceiptVouchers />} />
              <Route
                path="receipt-vouchers/add"
                element={<AddReceiptVoucher />}
              />
              <Route
                path="receipt-vouchers/edit/:id"
                element={<EditReceiptVoucher />}
              />
              <Route
                path="receipt-vouchers/view/:id"
                element={<ReceiptVoucherDetails />}
              />
              <Route path="payment-vouchers" element={<PaymentVouchers />} />
              <Route
                path="payment-vouchers/add"
                element={<AddPaymentVoucher />}
              />
              <Route
                path="payment-vouchers/edit/:id"
                element={<EditPaymentVoucher />}
              />
              <Route
                path="payment-vouchers/view/:id"
                element={<PaymentVoucherDetails />}
              />
              <Route path="notes-receivable" element={<NotesReceivable />} />
              <Route
                path="notes-receivable/add"
                element={<AddNoteReceivable />}
              />
              <Route
                path="notes-receivable/view/:id"
                element={<NoteReceivableDetails />}
              />
              <Route
                path="notes-receivable/edit/:id"
                element={<EditNoteReceivable />}
              />
              <Route path="notes-payable" element={<NotesPayable />} />
              <Route path="notes-payable/add" element={<AddNotePayable />} />
              <Route
                path="notes-payable/view/:id"
                element={<NotePayableDetails />}
              />
              <Route
                path="notes-payable/edit/:id"
                element={<EditNotePayable />}
              />
              <Route path="taxes" element={<Taxes />} />
              <Route path="taxes/add" element={<AddTax />} />
              <Route path="taxes/edit/:id" element={<EditTax />} />
              <Route path="taxes/view/:id" element={<TaxDetails />} />
              <Route path="opening-balance" element={<OpeningBalances />} />
              <Route
                path="opening-balance/add"
                element={<AddOpeningBalance />}
              />
              <Route
                path="opening-balance/edit/:id"
                element={<EditOpeningBalance />}
              />
              <Route
                path="opening-balance/view/:id"
                element={<OpeningBalanceDetails />}
              />
              <Route path="financial-periods" element={<FinancialPeriods />} />
              <Route
                path="financial-periods/add"
                element={<AddFinancialPeriod />}
              />
              <Route
                path="financial-periods/edit/:id"
                element={<EditFinancialPeriod />}
              />
              <Route
                path="financial-periods/view/:id"
                element={<FinancialPeriodDetails />}
              />
              <Route path="currencies" element={<Currencies />} />
              <Route path="currencies/add" element={<AddCurrency />} />
              <Route path="currencies/edit/:id" element={<EditCurrency />} />
              <Route path="currencies/view/:id" element={<CurrencyDetails />} />
              <Route path="exchange-rates" element={<ExchangeRates />} />
              <Route path="exchange-rates/add" element={<AddExchangeRate />} />
              <Route path="exchange-rates/:id" element={<EditExchangeRate />} />
              <Route
                path="exchange-rates/view/:id"
                element={<ExchangeRateDetails />}
              />
              <Route path="vendors" element={<Vendors />} />
              <Route path="vendors/add" element={<AddVendor />} />
              <Route path="vendors/edit/:id" element={<EditVendor />} />
              <Route path="vendors/view/:id" element={<VendorDetails />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/add" element={<AddCustomer />} />
              <Route path="customers/edit/:id" element={<EditCustomer />} />
              <Route path="customers/view/:id" element={<CustomerDetails />} />
              <Route path="banks" element={<Banks />} />
              <Route path="banks/add" element={<AddBank />} />
              <Route path="banks/edit/:id" element={<EditBank />} />
              <Route path="banks/view/:id" element={<BankDetails />} />
              <Route path="safes" element={<Safes />} />
              <Route path="safes/add" element={<AddSafe />} />
              <Route path="safes/edit/:id" element={<EditSafe />} />
              <Route path="safes/view/:id" element={<SafeDetails />} />
              <Route path="account-links" element={<AccountLinks />} />
              <Route path="trial-balance" element={<TrialBalance />} />
              <Route path="account-statement" element={<AccountStatement />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/add" element={<AddProject />} />
              <Route path="projects/edit/:id" element={<EditProject />} />
              <Route path="projects/view/:id" element={<ProjectDetails />} />
              <Route path="equipments" element={<Equipments />} />
              <Route path="equipments/add" element={<AddEquipment />} />
              <Route path="equipments/edit/:id" element={<EditEquipment />} />
              <Route path="equipments/view/:id" element={<ViewEquipment />} />
              <Route
                path="equipment-expenses"
                element={<EquipmentsExpenses />}
              />
              <Route
                path="equipment-expenses/add"
                element={<AddEquipmentExpenses />}
              />
              <Route
                path="equipment-expenses/edit/:equipmentId/:expenseId"
                element={<EditEquipmentExpenses />}
              />
              <Route
                path="equipment-expenses/view/:equipmentId/:expenseId"
                element={<ViewEquipmentExpenses />}
              />
              <Route path="contracts" element={<Contracts />} />
              <Route path="contracts/add" element={<AddContract />} />
              <Route
                path="contracts/from-quotation"
                element={<ContractFromQuotation />}
              />
              <Route
                path="contracts/from-quotation/:quotationId"
                element={<ContractFromQuotation />}
              />
              <Route path="contracts/edit/:id" element={<EditContract />} />
              <Route path="contracts/view/:id" element={<ViewContract />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="tasks/add" element={<AddTasks />} />
              <Route path="tasks/edit/:id" element={<EditTasks />} />
              <Route path="tasks/view/:id" element={<ViewTasks />} />
              <Route path="progress-bills" element={<ProgressBill />} />
              <Route path="progress-bills/add" element={<AddProgressBill />} />
              <Route
                path="progress-bills/edit/:id"
                element={<EditProgressBill />}
              />
              <Route
                path="progress-bills/view/:id"
                element={<ViewProgressBill />}
              />
              <Route path="lab-tests" element={<LabTests />} />
              <Route path="lab-tests/add" element={<AddLabTests />} />
              <Route path="lab-tests/edit/:id" element={<EditLabTests />} />
              <Route path="lab-tests/view/:id" element={<LabTestsDetails />} />
              <Route path="quotations" element={<Quotations />} />
              <Route path="quotations/add" element={<AddQuotation />} />
              <Route path="quotations/edit/:id" element={<AddQuotation />} />
              <Route
                path="quotations/view/:id"
                element={<QuotationDetails />}
              />
              <Route path="units" element={<Units />} />
              <Route path="units/add" element={<AddUnits />} />
              <Route path="units/edit/:id" element={<EditUnits />} />
              <Route path="units/view/:id" element={<UnitsDetails />} />
              <Route path="boreholes" element={<Boreholes />} />
              <Route path="boreholes/add" element={<AddBorehole />} />
              <Route path="boreholes/edit/:id" element={<AddBorehole />} />
              <Route path="boreholes/view/:id" element={<BoreholeDetails />} />
              <Route path="boreholes/defaults" element={<BoreholeDefaults />} />
              <Route path="boreholes/legends" element={<BoreholeLegends />} />
              <Route path="shifts" element={<Shifts />} />
              <Route path="shifts/add" element={<AddShift />} />
              <Route path="shifts/edit/:id" element={<EditShift />} />
              <Route path="shifts/details/:id" element={<ShiftDetails />} />
              <Route path="roles" element={<Roles />} />
              <Route path="roles/add" element={<AddRole />} />
              <Route path="roles/edit/:id" element={<EditRole />} />
              <Route
                path="roles/:id/permissions"
                element={<AssignPermissions />}
              />
              <Route path="website-settings" element={<WebsiteSettings />} />
              <Route path="website-messages" element={<WebsiteMessages />} />
              <Route path="permissions" element={<Permissions />} />
              <Route path="permissions/add" element={<AddPermission />} />
              <Route path="permissions/edit/:id" element={<EditPermission />} />
              <Route path="users" element={<Users />} />
              <Route path="users/add" element={<AddUser />} />
              <Route path="users/edit/:id" element={<EditUser />} />
              <Route path="users/:id/password" element={<ChangePassword />} />
              <Route path="users/:id" element={<UserDetails />} />
              <Route path="profile" element={<Profile />} />
              <Route path="positions" element={<JobTitles />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="audit-logs/:id" element={<ViewAuditLog />} />
              <Route path="audit-logs/cleanup" element={<CleanupAuditLogs />} />
              <Route path="audit-logs/compare" element={<CompareAuditLogs />} />
              <Route
                path="audit-logs/statistics"
                element={<AuditLogStatistics />}
              />
              <Route
                path="audit-logs/model/:modelType/:modelId"
                element={<ModelAuditLogs />}
              />
              {/* Project Reports */}
              <Route
                path="reports/projects/quotations"
                element={<QuotationsReport />}
              />
              <Route
                path="reports/projects/contracts"
                element={<ContractsReport />}
              />
              <Route
                path="reports/projects/progress-bills"
                element={<ProgressBillsReport />}
              />
              <Route path="reports/projects/tasks" element={<TasksReport />} />
              <Route
                path="reports/projects/overview"
                element={<ProjectsOverview />}
              />
              <Route
                path="reports/projects/:id/summary"
                element={<ProjectSummary />}
              />
              <Route path="project-tests" element={<ProjectTests />} />
              {/* Accounting Reports Routes */}
              <Route
                path="accounting-reports/income-statement"
                element={<IncomeStatement />}
              />
              <Route
                path="accounting-reports/customer-statement"
                element={<CustomerStatement />}
              />
              <Route
                path="accounting-reports/vendor-statement"
                element={<VendorStatement />}
              />
              <Route
                path="accounting-reports/balance-sheet"
                element={<BalanceSheet />}
              />
              <Route
                path="accounting-reports/cash-flow-statement"
                element={<CashFlowStatement />}
              />
              <Route
                path="accounting-reports/customer-aging"
                element={<CustomerAging />}
              />
              <Route
                path="accounting-reports/vendor-aging"
                element={<VendorAging />}
              />
              <Route
                path="accounting-reports/general-ledger"
                element={<GeneralLedger />}
              />
              <Route
                path="accounting-reports/cost-center-report"
                element={<CostCenterReport />}
              />
              <Route
                path="accounts/statement"
                element={<AccountStatementReport />}
              />
              <Route
                path="accounts/:id/statement"
                element={<AccountStatementReport />}
              />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={i18n.language === "ar"}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          limit={5}
          enableMultiContainer={false}
          stacked={false}
          style={{ zIndex: 9999999 }}
          toastStyle={{ zIndex: 9999999 }}
        />
      </Router>
    </TitleProvider>
  );
}

export default App;
